'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useEffectEvent, useRef, useState } from 'react';
import { applyWalletQuoteBalanceEvent } from '../../../cache/wallet-asset-entity.js';
import { decodeProfilePortfolioLifecycleEvent } from '../../../nats/profile-portfolio-event.js';
import { compareCursor, cursorKey, extractSequenceId } from '../../../nats/sequence-id.js';
import { useSdkNetworkScope } from '../../../provider/SdkRuntimeContext.js';
import { hashDiagnosticEntity } from '../../../tx-engine/diagnostics/lifecycle.js';
import { RegistryContext } from '../../context/RegistryContext.js';
import { sdkEvents } from '../../events.js';
import { reconcilePositionsAfterTrade } from '../reconcile-positions.js';
import { refetchRealtimeQuery } from '../refetchRealtimeQuery.js';
import { applyUserPortfolioSnapshotPoolEvent } from './pool-reducer.js';
import { userPortfolioHeldSubjects, userPortfolioRepresentedWallets, userPortfolioWalletEventIdentity, userPortfolioWalletSubjects } from './subjects.js';
import { isUserPortfolioSnapshotWire } from './types.js';
const WIRE_HANDOFF_RETRY_MS = [250, 500, 1_000, 2_000, 5_000];
const RESOLUTION_LIFECYCLE_COALESCE_MS = 100;
export function useUserPortfolioSnapshotRealtime({
  enabled,
  logger,
  onEvidence,
  knownWallets,
  snapshot,
  queryKey
}) {
  const registry = useContext(RegistryContext);
  const queryClient = useQueryClient();
  const networkScope = useSdkNetworkScope();
  const reconciliationRunning = useRef(false);
  const reconciliationDirty = useRef(false);
  const reconciliationDirtyReason = useRef(null);
  const reconciliationDirtyMarkets = useRef(new Set());
  const drainingEvents = useRef(false);
  const bufferedEvents = useRef([]);
  const bufferedEventKeys = useRef(new Set());
  const lastCursorBySubject = useRef(new Map());
  const pendingTransitionSignals = useRef({
    lifecycle: false,
    resolution: false
  });
  const pendingTransitionTimer = useRef(null);
  const pendingTransitionMarkets = useRef(new Set());
  const warnedFilterEdge = useRef(null);
  const warnedRegistryMissing = useRef(false);
  const warnedWalletWireVersion = useRef(null);
  const installedWalletKey = useRef('');
  const [walletReady, setWalletReady] = useState(null);
  const [walletWireRetry, setWalletWireRetry] = useState(0);
  const [heldReady, setHeldReady] = useState(null);
  const [reconciliationEpoch, setReconciliationEpoch] = useState(0);
  const representedWallets = userPortfolioRepresentedWallets(knownWallets, snapshot?.wallets ?? []);
  const walletSubjects = userPortfolioWalletSubjects(representedWallets);
  const walletKey = walletSubjects.join('\n');
  const held = snapshot === null ? null : userPortfolioHeldSubjects(snapshot);
  const heldKey = held?.allSubjects.join('\n') ?? '';
  const heldHandoffKey = `${String(reconciliationEpoch)}:${heldKey}`;
  const snapshotAnchor = snapshot?.recovery.streamSequence ?? '';
  // Handoff effects key on presence + anchor, not on the snapshot object,
  // whose identity changes on every live update without changing the handoff.
  const hasSnapshot = snapshot !== null;
  const positivePositionCount = held?.positivePositionCount ?? 0;
  const rateFilterCount = held?.rateSubjects.length ?? 0;
  const lifecycleFilterCount = held?.lifecycleSubjects.length ?? 0;
  const wireInterestVersion = registry?.getWireInterestVersion?.() ?? 0;
  const walletHandoffKey = `${String(wireInterestVersion)}:${walletKey}`;
  const isReady = !enabled || registry !== null && snapshot !== null && walletReady?.registry === registry && walletReady.key === walletHandoffKey && heldReady?.registry === registry && heldReady.key === heldHandoffKey;
  const emitRealtimeEvidence = useEffectEvent(evidence => {
    sdkEvents.emit('profile-portfolio:realtime', evidence);
    onEvidence?.(evidence);
  });
  const scheduleReconciliation = useEffectEvent((reason, markets = []) => {
    for (const market of markets) reconciliationDirtyMarkets.current.add(market);
    if (snapshot === null || reconciliationRunning.current || drainingEvents.current) {
      reconciliationDirty.current = true;
      reconciliationDirtyReason.current = reason;
      emitRealtimeEvidence({
        event: 'reconciliation_coalesced',
        reason
      });
      return;
    }
    reconciliationRunning.current = true;
    setHeldReady(null);
    emitRealtimeEvidence({
      event: 'reconciliation_scheduled',
      reason
    });
    const transitionedMarkets = [...reconciliationDirtyMarkets.current];
    reconciliationDirtyMarkets.current.clear();
    const outcome = reconcilePortfolio(queryClient, queryKey, reason, transitionedMarkets, market_0 => emitRealtimeEvidence({
      event: 'market_transition_refetch_failed',
      marketHash: hashDiagnosticEntity(market_0)
    }));
    void outcome.then(() => {
      reconciliationRunning.current = false;
      emitRealtimeEvidence({
        event: 'reconciliation_completed',
        reason
      });
      drainBufferedEvents();
      if (!reconciliationDirty.current) return;
      const trailingReason = reconciliationDirtyReason.current ?? reason;
      reconciliationDirty.current = false;
      reconciliationDirtyReason.current = null;
      scheduleReconciliation(trailingReason);
    }, () => {
      reconciliationRunning.current = false;
      emitRealtimeEvidence({
        event: 'reconciliation_failed',
        reason
      });
      // Events buffered behind the failed refetch must not wait for the next snapshot.
      drainBufferedEvents();
    });
  });
  const flushTransitionReconciliation = useEffectEvent(() => {
    const signals = pendingTransitionSignals.current;
    const markets_0 = [...pendingTransitionMarkets.current];
    pendingTransitionSignals.current = {
      lifecycle: false,
      resolution: false
    };
    pendingTransitionMarkets.current = new Set();
    pendingTransitionTimer.current = null;
    if (signals.lifecycle) {
      if (signals.resolution) emitRealtimeEvidence({
        event: 'resolution_coalesced'
      });
      scheduleReconciliation('lifecycle_transition', markets_0);
    } else if (signals.resolution) {
      scheduleReconciliation('resolution_transition', markets_0);
    }
  });
  const scheduleTransitionReconciliation = useEffectEvent((kind, subject) => {
    pendingTransitionSignals.current[kind] = true;
    const market_1 = transitionSubjectMarket(subject);
    if (market_1 !== null) pendingTransitionMarkets.current.add(market_1);
    if (pendingTransitionTimer.current !== null) clearTimeout(pendingTransitionTimer.current);
    pendingTransitionTimer.current = setTimeout(flushTransitionReconciliation, RESOLUTION_LIFECYCLE_COALESCE_MS);
  });
  const routeEvent = useEffectEvent((subject_0, payload, sequence) => {
    const prior = lastCursorBySubject.current.get(subject_0);
    if (prior !== undefined && compareCursor(sequence, prior) <= 0) return;
    lastCursorBySubject.current.set(subject_0, sequence);
    if (subject_0.endsWith('.lifecycle.v0')) {
      const lifecycle = decodeProfilePortfolioLifecycleEvent(subject_0, payload);
      if (lifecycle.status === 'ignored') return;
      if (lifecycle.status === 'reconciliation_required') {
        const reason_0 = lifecycle.reason === 'identity_invalid' ? 'event_identity_invalid' : 'event_payload_incomplete';
        emitRealtimeEvidence({
          event: 'event_failure',
          reason: reason_0
        });
        scheduleReconciliation(reason_0);
        return;
      }
      scheduleTransitionReconciliation('lifecycle', subject_0);
      return;
    }
    if (subject_0.endsWith('.resolution.v0')) {
      scheduleTransitionReconciliation('resolution', subject_0);
      return;
    }
    const walletEvent = userPortfolioWalletEventIdentity(subject_0);
    if (walletEvent !== null && representedWallets.includes(walletEvent.actorWallet)) {
      if (walletEvent.kind === 'quote_balance') {
        scheduleReconciliation('wallet_quote_balance_changed');
        return;
      }
      if (walletEvent.kind === 'claim') {
        scheduleReconciliation('wallet_claim_changed');
        return;
      }
      if (walletEvent.kind === 'backing' || walletEvent.kind === 'boost') {
        scheduleReconciliation('wallet_position_changed');
        return;
      }
    }
    const current = queryClient.getQueryData(queryKey);
    if (!isUserPortfolioSnapshotWire(current)) {
      emitRealtimeEvidence({
        event: 'event_failure',
        reason: 'event_payload_incomplete'
      });
      scheduleReconciliation('event_payload_incomplete');
      return;
    }
    const result = applyUserPortfolioSnapshotPoolEvent(current, representedWallets, subject_0, payload);
    if (result.status === 'applied') queryClient.setQueryData(queryKey, result.snapshot); // ALLOW-STATE-OWNERSHIP: exact snapshot owner
    if (result.status !== 'reconciliation_required') return;
    if (result.reason === 'represented_wallet_event') {
      scheduleReconciliation('wallet_position_changed');
      return;
    }
    emitRealtimeEvidence({
      event: 'event_failure',
      reason: result.reason
    });
    scheduleReconciliation(result.reason);
  });
  const drainBufferedEvents = useEffectEvent(() => {
    if (bufferedEvents.current.length === 0) return;
    const events = bufferedEvents.current.sort((left, right) => compareCursor(left.sequence, right.sequence));
    bufferedEvents.current = [];
    bufferedEventKeys.current.clear();
    drainingEvents.current = true;
    for (const event of events) routeEvent(event.subject, event.payload, event.sequence);
    drainingEvents.current = false;
  });
  const dispatchEvent = useEffectEvent((subject_1, payload_0) => {
    if (!(payload_0 instanceof Uint8Array)) {
      emitRealtimeEvidence({
        event: 'event_failure',
        reason: 'event_payload_incomplete'
      });
      scheduleReconciliation('event_payload_incomplete');
      return;
    }
    const sequence_0 = extractSequenceId(payload_0);
    if (sequence_0 === null) {
      emitRealtimeEvidence({
        event: 'event_failure',
        reason: 'sequence_gap'
      });
      scheduleReconciliation('sequence_gap');
      return;
    }
    // Live cash is the wallet-asset entity's own state; it must not wait on the snapshot gate.
    applyRepresentedQuoteBalance(subject_1, payload_0);
    if (snapshot === null || reconciliationRunning.current || drainingEvents.current) {
      bufferEvent(subject_1, payload_0, sequence_0, bufferedEvents.current, bufferedEventKeys.current);
      return;
    }
    routeEvent(subject_1, payload_0, sequence_0);
  });
  const applyRepresentedQuoteBalance = useEffectEvent((subject_2, payload_1) => {
    const walletEvent_0 = userPortfolioWalletEventIdentity(subject_2);
    if (walletEvent_0?.kind !== 'quote_balance') return;
    if (!representedWallets.includes(walletEvent_0.actorWallet)) return;
    const result_0 = applyWalletQuoteBalanceEvent(queryClient, networkScope, subject_2, payload_1, logger);
    if (result_0 !== 'invalid_event') return;
    emitRealtimeEvidence({
      event: 'event_failure',
      reason: 'event_payload_incomplete'
    });
  });
  const currentQueryKey = useEffectEvent(() => queryKey);
  const handleWireConsumerRecreated = useEffectEvent(() => {
    setHeldReady(null);
    setReconciliationEpoch(epoch => epoch + 1);
    scheduleReconciliation('consumer_recreated');
  });
  useEffect(() => {
    if (snapshot === null) return;
    drainBufferedEvents();
    if (!reconciliationDirty.current || reconciliationRunning.current) return;
    const reason_1 = reconciliationDirtyReason.current ?? 'wallet_position_changed';
    reconciliationDirty.current = false;
    reconciliationDirtyReason.current = null;
    scheduleReconciliation(reason_1);
  }, [snapshot]);
  useEffect(() => () => {
    if (pendingTransitionTimer.current !== null) clearTimeout(pendingTransitionTimer.current);
  }, []);
  useEffect(() => {
    if (!enabled || registry === null) return;
    return registry.onWireConsumerRecreated?.(handleWireConsumerRecreated);
  }, [enabled, registry]);
  useEffect(() => {
    if (!enabled) return;
    if (registry === null) {
      if (!warnedRegistryMissing.current) {
        warnedRegistryMissing.current = true;
        emitRealtimeEvidence({
          event: 'filter_install_failed',
          phase: 'wallet',
          filterCount: walletSubjects.length,
          reason: 'registry_unavailable',
          rateFilterCount: walletSubjects.length
        });
      }
      return;
    }
    const subjects = splitKey(walletKey);
    const handle = registry.acquireEntity(subjects, dispatchEvent, currentQueryKey(), handleWireConsumerRecreated);
    return () => handle.release();
  }, [enabled, registry, walletKey, walletSubjects.length]);
  const markWalletReady = useEffectEvent((readyRegistry, installedKey, readyKey) => {
    const walletSetChanged = snapshot !== null && installedWalletKey.current.length > 0 && installedWalletKey.current !== installedKey;
    installedWalletKey.current = installedKey;
    emitRealtimeEvidence({
      event: 'wallet_interest_ready',
      walletCount: representedWallets.length
    });
    setWalletReady({
      registry: readyRegistry,
      key: readyKey
    });
    if (walletSetChanged) {
      logger?.warn('profile_valuation.wallet_set_mismatch', {
        knownWalletCount: knownWallets.length,
        representedWalletCount: representedWallets.length
      });
      scheduleReconciliation('wallet_set_changed');
    }
  });
  useEffect(() => {
    if (!enabled || registry === null || !hasSnapshot) return;
    const activeRegistry = registry;
    let active = true;
    let retryTimer = null;
    const subjects_0 = splitKey(walletKey);
    async function completeHandoff() {
      try {
        const readiness = activeRegistry.awaitConcreteWireInterest?.(subjects_0);
        if (readiness !== null && readiness !== undefined) await readiness;
        const startSequence = checkedReplayStart(snapshotAnchor);
        if (startSequence === null) {
          scheduleReconciliation('replay_unavailable');
          return;
        }
        const replay = activeRegistry.replayConcreteWireInterest?.(subjects_0, startSequence);
        if (replay !== null && replay !== undefined) {
          const result_1 = await replay;
          if (result_1.status === 'reconciliation_required') {
            scheduleReconciliation('replay_unavailable');
            return;
          }
        }
        if (active) markWalletReady(activeRegistry, walletKey, walletHandoffKey);
      } catch (error) {
        // SILENT-CATCH: the redacted failure recorder below owns reporting for this wire edge.
        if (!active) return;
        if (!isWireInterestPending(error) && warnedWalletWireVersion.current !== wireInterestVersion) {
          warnedWalletWireVersion.current = wireInterestVersion;
          emitRealtimeEvidence({
            event: 'filter_install_failed',
            phase: 'wallet',
            filterCount: subjects_0.length,
            reason: 'wallet_wire_unavailable',
            rateFilterCount: subjects_0.length
          });
        }
        const retryIndex = Math.min(walletWireRetry, WIRE_HANDOFF_RETRY_MS.length - 1);
        retryTimer = setTimeout(() => setWalletWireRetry(attempt => attempt + 1), WIRE_HANDOFF_RETRY_MS[retryIndex]);
      }
    }
    void completeHandoff();
    return () => {
      active = false;
      if (retryTimer !== null) clearTimeout(retryTimer);
    };
  }, [enabled, hasSnapshot, registry, snapshotAnchor, walletHandoffKey, walletKey, walletWireRetry, wireInterestVersion]);
  useEffect(() => {
    if (!enabled || registry === null || !hasSnapshot) return;
    const subjects_1 = splitKey(heldKey);
    const handle_0 = registry.acquireEntity(subjects_1, dispatchEvent, currentQueryKey(), handleWireConsumerRecreated);
    const activeRegistry_0 = registry;
    let active_0 = true;
    if (positivePositionCount > 0 && rateFilterCount === 0) {
      const warningKey = `${snapshotAnchor}:${String(positivePositionCount)}`;
      if (warnedFilterEdge.current !== warningKey) {
        warnedFilterEdge.current = warningKey;
        emitRealtimeEvidence({
          event: 'positive_positions_without_rate_filters',
          positivePositionCount,
          rateFilterCount,
          lifecycleFilterCount,
          reason: 'no_complete_venue_identity'
        });
      }
    }
    async function completeHandoff_0() {
      try {
        const readiness_0 = activeRegistry_0.awaitConcreteWireInterest?.(subjects_1);
        if (readiness_0 !== null && readiness_0 !== undefined) await readiness_0;
        const startSequence_0 = checkedReplayStart(snapshotAnchor);
        if (startSequence_0 === null) {
          scheduleReconciliation('replay_unavailable');
          return;
        }
        const replay_0 = activeRegistry_0.replayConcreteWireInterest?.(subjects_1, startSequence_0);
        if (replay_0 !== null && replay_0 !== undefined) {
          const result_2 = await replay_0;
          if (result_2.status === 'reconciliation_required') {
            scheduleReconciliation('replay_unavailable');
            return;
          }
        }
        if (active_0) setHeldReady({
          registry: activeRegistry_0,
          key: heldHandoffKey
        });
      } catch {
        // SILENT-CATCH: the redacted failure recorder below owns reporting for this wire edge.
        if (!active_0) return;
        emitRealtimeEvidence({
          event: 'filter_install_failed',
          phase: 'held',
          filterCount: subjects_1.length,
          reason: 'held_wire_unavailable',
          rateFilterCount,
          lifecycleFilterCount
        });
        scheduleReconciliation('filter_install_failed');
      }
    }
    void completeHandoff_0();
    return () => {
      active_0 = false;
      handle_0.release();
    };
  }, [enabled, hasSnapshot, heldHandoffKey, heldKey, lifecycleFilterCount, positivePositionCount, rateFilterCount, registry, snapshotAnchor]);
  return {
    isReady
  };
}
async function reconcilePortfolio(queryClient, queryKey, reason, transitionedMarkets, onMarketRefetchFailed) {
  if (reason === 'lifecycle_transition' || reason === 'resolution_transition') {
    reconcilePositionsAfterTrade(queryClient);
  }
  // The realtime refetch joins an in-flight request, which may predate the
  // event being reconciled; its cursor is already admitted, so only a request
  // started after the join can cover it.
  const joinsEarlierFetch = queryClient.getQueryState(queryKey)?.fetchStatus === 'fetching';
  await refetchPortfolioSnapshot(queryClient, queryKey);
  if (joinsEarlierFetch) await refetchPortfolioSnapshot(queryClient, queryKey);
  await Promise.all(transitionedMarkets.map(async market => {
    const outcome = await refetchRealtimeQuery(queryClient, ['markets', 'entity', market]);
    if (outcome === 'error') onMarketRefetchFailed(market);
  }));
}
async function refetchPortfolioSnapshot(queryClient, queryKey) {
  const outcome = await refetchRealtimeQuery(queryClient, queryKey);
  if (outcome !== 'success') throw new Error(`Portfolio snapshot reconciliation ${outcome}`);
}
function bufferEvent(subject, payload, sequence, events, keys) {
  const key = `${subject}:${cursorKey(sequence)}`;
  if (keys.has(key)) return;
  keys.add(key);
  events.push({
    subject,
    payload,
    sequence
  });
}
function checkedReplayStart(value) {
  if (!/^(?:0|[1-9]\d*)$/.test(value)) return null;
  const start = BigInt(value) + BigInt(1);
  return start <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(start) : null;
}
function isWireInterestPending(error) {
  return error !== null && typeof error === 'object' && 'reason' in error && error.reason === 'not_ready';
}
/** `morfi.market.{market}.lifecycle.v0` / `.resolution.v0` → the market pubkey. */
function transitionSubjectMarket(subject) {
  const parts = subject.split('.');
  const market = parts[2];
  return parts[0] === 'morfi' && parts[1] === 'market' && market !== undefined && market.length > 0 ? market : null;
}
function splitKey(value) {
  return value.length === 0 ? [] : value.split('\n');
}